'use client';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { IoChevronDown, IoClose } from 'react-icons/io5';

import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import { Modal, Spinner } from '@benzinga/core-ui';
import { TickerSearch } from '../TickerSearch';
import { parseTickerInput, removeExistingTickers } from '../portfolioActionUtils';
import { PortfolioModalHeader } from './PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalFooterClass,
  portfolioModalShellClass,
} from './modalStyles';

const suggestedTickers = ['AAPL', 'MSFT', 'NVDA', 'GOOGL', 'AMZN', 'META', 'TSLA', 'AVGO', 'AMD'];

export const AddTicker = () => {
  const { addTickerModal, setAddTickerModal, watchlists, selectedWatchlist } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const wasModalOpen = useRef(false);
  const [watchlistToAddTo, setWatchlistToAddTo] = useState<Watchlist | null>(
    selectedWatchlist && selectedWatchlist.watchlistId !== 'overview' ? selectedWatchlist : null,
  );
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [pastedTickers, setPastedTickers] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const selectedTickerSet = useMemo(() => new Set(selectedTickers), [selectedTickers]);

  const tickersToAdd = useMemo(
    () =>
      removeExistingTickers([...selectedTickers, ...parseTickerInput(pastedTickers)], watchlistToAddTo?.symbols ?? []),
    [pastedTickers, selectedTickers, watchlistToAddTo?.symbols],
  );

  const resetModal = useCallback(() => {
    setAddTickerModal({ show: false, ticker: null, isLoading: false });
    setWatchlistToAddTo(null);
    setSelectedTickers([]);
    setPastedTickers('');
    setShowPaste(false);
  }, [setAddTickerModal]);

  const selectTicker = (ticker: string) => {
    setSelectedTickers(currentTickers =>
      currentTickers.includes(ticker) ? currentTickers : [...currentTickers, ticker],
    );
  };

  const handleAddTicker = useCallback(async () => {
    if (!watchlistToAddTo) return;
    if (tickersToAdd.length === 0) {
      toast.error('Add at least one ticker that is not already in this watchlist.');
      return;
    }

    setAddTickerModal({ show: true, ticker: null, isLoading: true });
    try {
      const result = await watchlistManager.addTickersToWatchlist(watchlistToAddTo, tickersToAdd);
      if (result.err) {
        toast.error('Unable to add tickers. Please try again.');
        setAddTickerModal({ show: true, ticker: null, isLoading: false });
        return;
      }

      toast.success(
        `${tickersToAdd.length} ${tickersToAdd.length === 1 ? 'ticker' : 'tickers'} added to ${watchlistToAddTo.name}`,
      );
      resetModal();
    } catch (error) {
      console.error('Error adding tickers to watchlist:', error);
      toast.error('Unable to add tickers. Please try again.');
      setAddTickerModal({ show: true, ticker: null, isLoading: false });
    }
  }, [resetModal, setAddTickerModal, tickersToAdd, watchlistManager, watchlistToAddTo]);

  useEffect(() => {
    if (addTickerModal.show && !wasModalOpen.current) {
      setWatchlistToAddTo(selectedWatchlist && selectedWatchlist.watchlistId !== 'overview' ? selectedWatchlist : null);
      setSelectedTickers(addTickerModal.ticker ? [addTickerModal.ticker] : []);
      setPastedTickers('');
      setShowPaste(false);
    }
    wasModalOpen.current = addTickerModal.show;
  }, [addTickerModal.show, addTickerModal.ticker, selectedWatchlist]);

  return (
    <Modal
      visible={addTickerModal.show}
      onClose={resetModal}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { width: '520px', maxWidth: 'calc(100vw - 32px)', height: 'auto' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        <PortfolioModalHeader title="Add tickers" onClose={resetModal} />
        <div className={`${portfolioModalBodyClass} flex flex-col gap-4`}>
          <div className="flex items-center gap-3">
            <label className="shrink-0 text-sm text-slate-600" htmlFor="add-tickers-watchlist">
              Adding to
            </label>
            <div className="relative min-w-0 flex-1">
              <select
                id="add-tickers-watchlist"
                className="min-h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-10 text-sm text-slate-900 transition-colors hover:border-slate-300 focus:border-bzblue-500 focus:outline-none focus:ring-2 focus:ring-bzblue-100 disabled:cursor-not-allowed disabled:opacity-50"
                value={watchlistToAddTo?.watchlistId ?? ''}
                disabled={addTickerModal.isLoading}
                onChange={event =>
                  setWatchlistToAddTo(
                    watchlists.find(watchlist => watchlist.watchlistId === event.target.value) ?? null,
                  )
                }
              >
                <option value="">Select a watchlist</option>
                {watchlists.map(watchlist => (
                  <option key={watchlist.watchlistId} value={watchlist.watchlistId}>
                    {watchlist.name}
                  </option>
                ))}
              </select>
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400"
              >
                <IoChevronDown size={16} />
              </span>
            </div>
          </div>

          <div>
            <div className="rounded-lg border border-slate-200 bg-white focus-within:border-bzblue-500 focus-within:ring-2 focus-within:ring-bzblue-100">
              <TickerSearch placeholder="Search ticker or company" onTickerClick={selectTicker} hideType />
            </div>

            {selectedTickers.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {selectedTickers.map(ticker => (
                  <span
                    key={ticker}
                    className="inline-flex h-8 items-center gap-1 rounded-md bg-bzblue-100 px-2.5 text-xs font-bold text-bzblue-800"
                  >
                    {ticker}
                    <button
                      type="button"
                      aria-label={`Remove ${ticker}`}
                      className="rounded p-0.5 hover:bg-bzblue-300/40"
                      onClick={() =>
                        setSelectedTickers(currentTickers => currentTickers.filter(item => item !== ticker))
                      }
                    >
                      <IoClose size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold text-slate-500">Popular</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedTickers.map(ticker =>
                selectedTickerSet.has(ticker) ? null : (
                  <button
                    type="button"
                    key={ticker}
                    className="h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:border-bzblue-400 hover:bg-bzblue-100 hover:text-bzblue-800"
                    onClick={() => selectTicker(ticker)}
                  >
                    {ticker}
                  </button>
                ),
              )}
            </div>
          </div>

          <div>
            {!showPaste ? (
              <button
                type="button"
                className="text-sm font-semibold text-bzblue-700 hover:text-bzblue-800"
                onClick={() => setShowPaste(true)}
              >
                Or paste a list of tickers
              </button>
            ) : (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-500" htmlFor="watchlist-ticker-paste">
                    Paste a list
                  </label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                    onClick={() => {
                      setShowPaste(false);
                      setPastedTickers('');
                    }}
                  >
                    Hide
                  </button>
                </div>
                <textarea
                  id="watchlist-ticker-paste"
                  className="min-h-20 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-bzblue-500 focus:outline-none focus:ring-2 focus:ring-bzblue-100"
                  placeholder="AAPL, MSFT, NVDA"
                  value={pastedTickers}
                  disabled={addTickerModal.isLoading}
                  onChange={event => setPastedTickers(event.target.value)}
                />
                <p className="mt-1 text-xs text-slate-500">Separate with commas, spaces, or new lines.</p>
              </div>
            )}
          </div>
        </div>

        <div className={`${portfolioModalFooterClass} sm:justify-between`}>
          <p className="text-xs text-slate-500 sm:text-sm">
            {tickersToAdd.length > 0
              ? `${tickersToAdd.length} ready to add`
              : 'Search or choose a ticker to get started'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={resetModal}
              disabled={addTickerModal.isLoading}
              className="h-10 flex-1 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddTicker}
              disabled={addTickerModal.isLoading || !watchlistToAddTo || tickersToAdd.length === 0}
              className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-bzblue-700 px-4 text-sm font-bold text-white hover:bg-bzblue-800 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            >
              {addTickerModal.isLoading ? (
                <Spinner size={16} />
              ) : tickersToAdd.length > 0 ? (
                `Add ${tickersToAdd.length} ${tickersToAdd.length === 1 ? 'ticker' : 'tickers'}`
              ) : (
                'Add tickers'
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
